import React, { useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  Badge,
  Form,
  Navbar,
  Nav,
  Ratio,
} from "react-bootstrap";

/**
 * 5280.solar — Landing Page
 * Tech: React + TypeScript + React‑Bootstrap
 * Version: v1.0 (2025-08-23)
 * Notes:
 * - Add Bootstrap CSS import once in your app entry (main.tsx or index.tsx):
 *     import 'bootstrap/dist/css/bootstrap.min.css';
 * - Replace placeholder images/links, and wire the form submit to your API.
 */

// -----------------------------
// Local helper types
// -----------------------------

type Feature = {
  id: string;
  title: string;
  desc: string;
  icon?: React.ReactNode;
};

type FAQ = {
  q: string;
  a: string | React.ReactNode;
};

// -----------------------------
// Content (edit freely)
// -----------------------------

const FEATURES: Feature[] = [
  {
    id: "mile-high-savings",
    title: "Mile‑High Savings",
    desc: "Leverage 300+ sunny days a year—cut your bill and lock in long‑term power costs.",
  },
  {
    id: "fast-track-permits",
    title: "Fast‑Track Permits",
    desc: "Front Range‑savvy designers who know the AHJ rules from Denver to Fort Collins.",
  },
  {
    id: "top-tier-gear",
    title: "Top‑Tier Gear",
    desc: "Tier‑1 panels, high‑efficiency inverters, and sleek all‑black arrays built for Colorado.",
  },
  {
    id: "local-pros",
    title: "Local Pros",
    desc: "Vetted installers, NABCEP‑certified crews, and Colorado‑first customer care.",
  },
];

const FAQS: FAQ[] = [
  {
    q: "How much can a Denver‑area home save?",
    a: "Typical customers see 20–60% savings over utility rates, depending on roof, shade, and usage.",
  },
  {
    q: "What incentives are available?",
    a: (
      <>
        You may be eligible for the federal clean energy tax credit and local utility rebates. We'll include these in your
        custom proposal.
      </>
    ),
  },
  {
    q: "What about snow and hail?",
    a: "We design for Colorado weather—hail‑rated panels, racking for wind loads, and production modeling with seasonal effects.",
  },
  {
    q: "Do you offer batteries?",
    a: "Yes. We pair solar with whole‑home batteries for backup and TOU optimization.",
  },
];

// -----------------------------
// Utility
// -----------------------------

function currency(n: number) {
  return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

// Very light estimator (educational only)
function estimateSavings(avgBill: number, offsetPct: number) {
  const annual = avgBill * 12;
  const offset = annual * (offsetPct / 100);
  const years25 = offset * 25; // rough lifetime savings before escalators
  return { annual, offset, years25 };
}

// -----------------------------
// Main Component
// -----------------------------

const SOLAR_5280_LandingPage_v01: React.FC = () => {
  const [avgBill, setAvgBill] = useState<number>(150);
  const [offsetPct, setOffsetPct] = useState<number>(80);
  const { annual, offset, years25 } = useMemo(() => estimateSavings(avgBill, offsetPct), [avgBill, offsetPct]);

  return (
    <div>
      {/* Navbar */}
      <Navbar bg="light" expand="md" className="border-bottom sticky-top">
        <Container>
          <Navbar.Brand className="fw-bold">
            <span className="text-primary">5280</span>
            <span className="text-dark">.solar</span>
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="nav" />
          <Navbar.Collapse id="nav">
            <Nav className="ms-auto">
              <Nav.Link href="#features">Why Solar</Nav.Link>
              <Nav.Link href="#calculator">Savings</Nav.Link>
              <Nav.Link href="#gallery">Gallery</Nav.Link>
              <Nav.Link href="#faq">FAQ</Nav.Link>
              <Nav.Link href="#contact" className="fw-semibold">Get Quote</Nav.Link>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Hero */}
      <section className="py-5 bg-light border-bottom">
        <Container>
          <Row className="align-items-center g-4">
            <Col md={6}>
              <Badge bg="warning" text="dark" className="mb-3">Colorado‑First Solar</Badge>
              <h1 className="display-5 fw-bold">Go Solar the Mile‑High Way</h1>
              <p className="lead text-secondary">
                From Denver bungalows to mountain moderns—custom designs, premium hardware, and local crews you can trust.
              </p>
              <div className="d-flex flex-wrap gap-2">
                <Button href="#contact" variant="primary" size="lg">Get My Free Quote</Button>
                <Button href="#calculator" variant="outline-secondary" size="lg">Estimate Savings</Button>
              </div>
              <div className="mt-3 small text-muted">No spam. No pushy sales. Just clear numbers.</div>
            </Col>
            <Col md={6}>
              <Card className="shadow-sm">
                <Ratio aspectRatio="16x9">
                  <img
                    src="https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?q=80&w=1400&auto=format&fit=crop"
                    alt="All‑black solar array on a Colorado home"
                    style={{ objectFit: "cover" }}
                  />
                </Ratio>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Trust bar */}
      <section className="py-4">
        <Container>
          <Row className="text-center g-3">
            {[
              "NABCEP‑Certified",
              "Tier‑1 Panels",
              "25‑Year Warranty",
              "Local Utility Expertise",
              "BBB A+ (target)",
            ].map((t) => (
              <Col key={t} xs={6} md={2} className="mx-auto">
                <Badge bg="light" text="dark" className="border w-100 py-2">{t}</Badge>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Features */}
      <section id="features" className="py-5 bg-white">
        <Container>
          <Row className="mb-4">
            <Col>
              <h2 className="fw-bold">Built for Colorado Homes</h2>
              <p className="text-secondary m-0">Local knowledge, faster installs, smarter designs.</p>
            </Col>
          </Row>
          <Row className="g-4">
            {FEATURES.map((f) => (
              <Col key={f.id} md={6} lg={3}>
                <Card className="h-100 shadow-sm">
                  <Card.Body>
                    <div className="mb-2">{f.icon}</div>
                    <Card.Title>{f.title}</Card.Title>
                    <Card.Text className="text-secondary">{f.desc}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Calculator */}
      <section id="calculator" className="py-5 bg-light border-top border-bottom">
        <Container>
          <Row className="mb-4">
            <Col md={8}>
              <h2 className="fw-bold">Quick Savings Estimator</h2>
              <p className="text-secondary m-0">Plug in your bill and a target offset to preview potential savings.</p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={6}>
              <Card className="shadow-sm h-100">
                <Card.Body>
                  <Form>
                    <Form.Group className="mb-3" controlId="avgBill">
                      <Form.Label>Average Monthly Electric Bill</Form.Label>
                      <Form.Control
                        type="number"
                        min={50}
                        step={10}
                        value={avgBill}
                        onChange={(e) => setAvgBill(Number(e.target.value || 0))}
                        placeholder="e.g., 150"
                      />
                      <Form.Text className="text-muted">$50–$800 typical</Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="offsetPct">
                      <Form.Label>Target Solar Offset (%)</Form.Label>
                      <Form.Range
                        min={30}
                        max={100}
                        step={5}
                        value={offsetPct}
                        onChange={(e) => setOffsetPct(Number(e.target.value))}
                      />
                      <div className="d-flex justify-content-between small text-muted">
                        <span>30%</span>
                        <span>{offsetPct}%</span>
                        <span>100%</span>
                      </div>
                    </Form.Group>

                    <div className="d-grid gap-2">
                      <Button href="#contact" variant="primary">Get a Custom Proposal</Button>
                      <Button href="#faq" variant="outline-secondary">How is this calculated?</Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="shadow-sm h-100">
                <Card.Body>
                  <h5 className="mb-3">Your Rough Numbers</h5>
                  <div className="d-grid gap-2">
                    <div className="d-flex justify-content-between">
                      <span className="text-secondary">Current annual spend</span>
                      <strong>{currency(annual)}</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span className="text-secondary">Potential annual offset</span>
                      <strong className="text-success">{currency(offset)}</strong>
                    </div>
                    <div className="d-flex justify-content-between border-top pt-2">
                      <span className="text-secondary">25‑year savings (rough)</span>
                      <strong className="text-primary">{currency(years25)}</strong>
                    </div>
                  </div>
                  <p className="small text-muted mt-3">
                    Educational estimate only; not a quote. Actual results vary by roof, shade, utility, and equipment.
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Gallery */}
      <section id="gallery" className="py-5">
        <Container>
          <Row className="mb-4">
            <Col>
              <h2 className="fw-bold">Recent Colorado Installs</h2>
              <p className="text-secondary m-0">Clean curb appeal. Optimized production.</p>
            </Col>
          </Row>
          <Row className="g-4">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <Col key={n} xs={12} sm={6} lg={4}>
                <Card className="shadow-sm">
                  <Ratio aspectRatio="16x9">
                    <img
                      src={`https://images.unsplash.com/photo-150${80 + n}514177221-188b1cf16e9d?q=80&w=1200&auto=format&fit=crop`}
                      alt={`Colorado solar project ${n}`}
                      style={{ objectFit: "cover" }}
                    />
                  </Ratio>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Testimonials */}
      <section className="py-5 bg-light border-top border-bottom">
        <Container>
          <Row className="mb-4">
            <Col>
              <h2 className="fw-bold">Neighbors are Switching</h2>
              <p className="text-secondary m-0">Real feedback from Front Range homeowners.</p>
            </Col>
          </Row>
          <Row className="g-4">
            {[
              {
                name: "Lindsey R.",
                text: "Clean install in Wash Park—crew was on time and the design looks fantastic.",
              },
              { name: "Arjun P.", text: "We hit 92% offset in Lafayette. Summer bills are finally predictable." },
              {
                name: "Diego M.",
                text: "Battery kept the essentials on during a storm outage. Worth it.",
              },
            ].map((t) => (
              <Col key={t.name} md={4}>
                <Card className="h-100 shadow-sm">
                  <Card.Body>
                    <Card.Text className="mb-2">“{t.text}”</Card.Text>
                    <div className="small text-muted">— {t.name}</div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-5 bg-white">
        <Container>
          <Row className="mb-4">
            <Col>
              <h2 className="fw-bold">FAQ</h2>
            </Col>
          </Row>
          <Row>
            <Col md={10} lg={8}>
              {FAQS.map((f) => (
                <Card key={f.q} className="mb-3 shadow-sm">
                  <Card.Body>
                    <Card.Title className="fs-5">{f.q}</Card.Title>
                    <Card.Text className="text-secondary">{f.a}</Card.Text>
                  </Card.Body>
                </Card>
              ))}
            </Col>
          </Row>
        </Container>
      </section>

      {/* Contact */}
      <section id="contact" className="py-5 bg-light border-top">
        <Container>
          <Row className="mb-4">
            <Col>
              <h2 className="fw-bold">Get Your Free Solar Proposal</h2>
              <p className="text-secondary m-0">Tell us a bit about your home. We’ll run the numbers.</p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={6}>
              <Card className="shadow-sm h-100">
                <Card.Body>
                  <Form onSubmit={(e) => { e.preventDefault(); alert("Submitted! Replace with API call."); }}>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group controlId="firstName">
                          <Form.Label>First Name</Form.Label>
                          <Form.Control placeholder="First name" required />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="lastName">
                          <Form.Label>Last Name</Form.Label>
                          <Form.Control placeholder="Last name" required />
                        </Form.Group>
                      </Col>
                      <Col md={8}>
                        <Form.Group controlId="email">
                          <Form.Label>Email</Form.Label>
                          <Form.Control type="email" placeholder="you@example.com" required />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group controlId="phone">
                          <Form.Label>Phone</Form.Label>
                          <Form.Control placeholder="(555) 555‑5555" />
                        </Form.Group>
                      </Col>
                      <Col md={8}>
                        <Form.Group controlId="address">
                          <Form.Label>Street Address</Form.Label>
                          <Form.Control placeholder="1234 Elm St" required />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group controlId="zip">
                          <Form.Label>ZIP</Form.Label>
                          <Form.Control placeholder="80202" required />
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group controlId="notes">
                          <Form.Label>Notes (optional)</Form.Label>
                          <Form.Control as="textarea" rows={3} placeholder="Roof type, shade, battery interest, etc." />
                        </Form.Group>
                      </Col>
                    </Row>
                    <div className="d-grid gap-2 mt-3">
                      <Button type="submit" variant="primary" size="lg">Request My Proposal</Button>
                      <div className="small text-muted text-center">By submitting, you agree to be contacted about your quote.</div>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="shadow-sm h-100">
                <Card.Body>
                  <h5 className="mb-3">Why 5280.solar?</h5>
                  <ul className="text-secondary">
                    <li>Local expertise from Denver to the Foothills</li>
                    <li>Premium components with long warranties</li>
                    <li>Clear proposals and real production modeling</li>
                    <li>Battery options for backup and rate optimization</li>
                  </ul>
                  <div className="ratio ratio-16x9 rounded overflow-hidden">
                    <iframe
                      title="Map placeholder"
                      src="https://www.openstreetmap.org/export/embed.html?bbox=-105.33%2C39.55%2C-104.6%2C39.95&layer=mapnik"
                      style={{ border: 0 }}
                    />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Footer */}
      <footer className="py-4 bg-white border-top">
        <Container>
          <Row className="align-items-center">
            <Col md={6} className="small text-muted">
              © {new Date().getFullYear()} 5280.solar — All rights reserved.
            </Col>
            <Col md={6} className="small text-muted text-md-end">
              <a href="#privacy" className="text-decoration-none me-3">Privacy</a>
              <a href="#terms" className="text-decoration-none">Terms</a>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
};

export default SOLAR_5280_LandingPage_v01;
